import React, { useState, useEffect, useCallback } from "react";
import {
    getMyProjects, getProjectTasks, Project, Task,
    getTimesheetTemplates, getTimesheetTemplateDetail,
    createTimesheetTemplate, updateTimesheetTemplate, deleteTimesheetTemplate,
    TimesheetTemplateSummary, TimesheetTemplateLine,
} from "../services/api";

const primaryBtnStyle: React.CSSProperties = {
    backgroundColor: "#F18E00", color: "white", border: "none", borderRadius: "4px",
    padding: "10px 24px", cursor: "pointer", fontSize: "14px", fontWeight: "bold",
};
const secondaryBtnStyle: React.CSSProperties = {
    backgroundColor: "white", color: "#5C5D5E", border: "1px solid #ddd",
    borderRadius: "4px", padding: "10px 24px", cursor: "pointer", fontSize: "14px",
};
const smallBtnStyle: React.CSSProperties = {
    backgroundColor: "white", color: "#5C5D5E", border: "1px solid #ddd",
    borderRadius: "4px", padding: "6px 12px", cursor: "pointer", fontSize: "12px",
};
const inputStyle: React.CSSProperties = {
    padding: "8px 10px", fontSize: "13px", border: "1px solid #ddd", borderRadius: "4px",
    width: "100%", boxSizing: "border-box",
};
const thLeftStyle: React.CSSProperties = {
    padding: "10px 14px", textAlign: "left", borderBottom: "2px solid #e0e0e0",
    fontSize: "12px", color: "#5C5D5E", textTransform: "uppercase",
};
const tdLeftStyle: React.CSSProperties = {
    padding: "10px 14px", textAlign: "left", borderBottom: "1px solid #eee", fontSize: "13px",
};

// A row being edited -- carries a client-only id so React keys stay stable
// while the user is still picking project/task, distinct from the line's
// real DB id (which only exists after the template has been saved once).
interface EditLine {
    clientId: string;
    projectId: number | null;
    taskId: number | null;
    hours: string;
}

const newEditLine = (): EditLine => ({
    clientId: Math.random().toString(36).substr(2, 9),
    projectId: null,
    taskId: null,
    hours: "",
});

const TimesheetTemplates: React.FC = () => {
    const [templates, setTemplates] = useState<TimesheetTemplateSummary[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [projects, setProjects] = useState<Project[]>([]);
    const [taskCache, setTaskCache] = useState<Record<number, Task[]>>({});

    // null = list view. -1 = creating a brand-new template. Any other
    // number = editing that existing template's id.
    const [editingId, setEditingId] = useState<number | null | -1>(null);
    const [editName, setEditName] = useState("");
    const [editLines, setEditLines] = useState<EditLine[]>([]);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);

    const loadTemplates = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getTimesheetTemplates();
            setTemplates(data.templates);
        } catch (e) {
            console.error("Failed to load templates", e);
            setError("Failed to load templates. Please try again.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadTemplates();
        getMyProjects().then(setProjects).catch(e => console.error("Failed to load projects", e));
    }, [loadTemplates]);

    const loadTasks = async (projectId: number) => {
        if (taskCache[projectId]) return;
        try {
            const tasks = await getProjectTasks(projectId);
            setTaskCache(prev => ({ ...prev, [projectId]: tasks }));
        } catch (e) {
            console.error("Failed to load tasks", e);
        }
    };

    const startCreate = () => {
        setEditingId(-1);
        setEditName("");
        setEditLines([newEditLine()]);
        setSaveError(null);
    };

    const startEdit = async (templateId: number) => {
        setSaveError(null);
        try {
            const detail = await getTimesheetTemplateDetail(templateId);
            setEditName(detail.name);
            setEditLines(
                detail.lines.length > 0
                    ? detail.lines.map((l: TimesheetTemplateLine) => ({
                        clientId: Math.random().toString(36).substr(2, 9),
                        projectId: l.project_id,
                        taskId: l.task_id,
                        hours: l.hours != null ? String(l.hours) : "",
                    }))
                    : [newEditLine()]
            );
            detail.lines.forEach(l => loadTasks(l.project_id));
            setEditingId(templateId);
        } catch (e) {
            console.error("Failed to load template", e);
            setError("Failed to load template for editing.");
        }
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditName("");
        setEditLines([]);
        setSaveError(null);
    };

    const updateLine = (clientId: string, field: keyof EditLine, value: any) => {
        setEditLines(prev => prev.map(l => {
            if (l.clientId !== clientId) return l;
            const updated = { ...l, [field]: value };
            if (field === "projectId") {
                updated.taskId = null;
                if (value) loadTasks(value);
            }
            return updated;
        }));
    };

    const addLine = () => setEditLines(prev => [...prev, newEditLine()]);

    const removeLine = (clientId: string) => {
        setEditLines(prev => {
            const remaining = prev.filter(l => l.clientId !== clientId);
            return remaining.length ? remaining : [newEditLine()];
        });
    };

    const validLines = editLines.filter(l => l.projectId && l.taskId);

    const handleSave = async () => {
        if (!editName.trim() || validLines.length === 0) return;
        setSaving(true);
        setSaveError(null);
        try {
            const payload = {
                name: editName.trim(),
                lines: validLines.map(l => ({
                    project_id: l.projectId as number,
                    task_id: l.taskId as number,
                    hours: l.hours.trim() === "" ? null : parseFloat(l.hours),
                })),
            };
            if (editingId === -1) {
                await createTimesheetTemplate(payload);
            } else if (editingId !== null) {
                await updateTimesheetTemplate(editingId, payload);
            }
            cancelEdit();
            await loadTemplates();
        } catch (e) {
            console.error("Failed to save template", e);
            setSaveError("Failed to save template. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (templateId: number) => {
        if (!window.confirm("Delete this template?")) return;
        try {
            await deleteTimesheetTemplate(templateId);
            await loadTemplates();
        } catch (e) {
            console.error("Failed to delete template", e);
        }
    };

    if (loading) return <div>Loading...</div>;

    if (editingId !== null) {
        return (
            <div>
                <h1 style={{ color: "#5C5D5E", marginTop: 0 }}>
                    {editingId === -1 ? "New Template" : "Edit Template"}
                </h1>

                <div style={{ marginBottom: "16px", maxWidth: "400px" }}>
                    <label style={{ fontSize: "12px", color: "#5C5D5E", display: "block", marginBottom: "4px" }}>
                        Template Name
                    </label>
                    <input
                        type="text" value={editName} onChange={e => setEditName(e.target.value)}
                        placeholder="e.g. Typical Week" style={inputStyle}
                    />
                </div>

                <div style={{ overflowX: "auto", border: "1px solid #e0e0e0", borderRadius: "6px", marginBottom: "16px" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                            <tr>
                                <th style={thLeftStyle}>Project</th>
                                <th style={thLeftStyle}>Task</th>
                                <th style={thLeftStyle}>Default Hours (optional)</th>
                                <th style={thLeftStyle}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {editLines.map(line => (
                                <tr key={line.clientId}>
                                    <td style={tdLeftStyle}>
                                        <select
                                            value={line.projectId ?? ""}
                                            onChange={e => updateLine(line.clientId, "projectId", e.target.value ? Number(e.target.value) : null)}
                                            style={inputStyle}
                                        >
                                            <option value="">Select project...</option>
                                            {projects.map(p => (
                                                <option key={p.id} value={p.id}>{p.name}</option>
                                            ))}
                                        </select>
                                    </td>
                                    <td style={tdLeftStyle}>
                                        <select
                                            value={line.taskId ?? ""}
                                            onChange={e => updateLine(line.clientId, "taskId", e.target.value ? Number(e.target.value) : null)}
                                            disabled={!line.projectId}
                                            style={inputStyle}
                                        >
                                            <option value="">Select task...</option>
                                            {(line.projectId ? taskCache[line.projectId] || [] : []).map(t => (
                                                <option key={t.id} value={t.id}>{t.name}</option>
                                            ))}
                                        </select>
                                    </td>
                                    <td style={tdLeftStyle}>
                                        <input
                                            type="number" step="0.25" min="0" max="24" value={line.hours}
                                            onChange={e => updateLine(line.clientId, "hours", e.target.value)}
                                            placeholder="Blank until entered" style={inputStyle}
                                        />
                                    </td>
                                    <td style={tdLeftStyle}>
                                        <button onClick={() => removeLine(line.clientId)} style={{ background: "none", border: "none", color: "#999", cursor: "pointer", fontSize: "16px" }}>x</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <button onClick={addLine} style={{ ...smallBtnStyle, marginBottom: "24px" }}>+ Add Line</button>

                <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                    <button
                        onClick={handleSave}
                        disabled={saving || !editName.trim() || validLines.length === 0}
                        style={{ ...primaryBtnStyle, opacity: (saving || !editName.trim() || validLines.length === 0) ? 0.5 : 1 }}
                    >
                        {saving ? "Saving…" : "Save Template"}
                    </button>
                    <button onClick={cancelEdit} style={secondaryBtnStyle}>Cancel</button>
                    {saveError && <span style={{ color: "#C00000", fontSize: "13px" }}>{saveError}</span>}
                </div>
            </div>
        );
    }

    return (
        <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                <h1 style={{ color: "#5C5D5E", margin: 0 }}>Timesheet Templates</h1>
                <button onClick={startCreate} style={primaryBtnStyle}>+ New Template</button>
            </div>
            <p style={{ color: "#666", fontSize: "13px", marginTop: 0, marginBottom: "20px" }}>
                Build a reusable set of projects/tasks, then apply it to any week from the Timesheet Entry screen with the "Apply Template" button.
            </p>

            {error && <div style={{ color: "#C00000", fontSize: "14px", marginBottom: "12px" }}>{error}</div>}

            {templates.length === 0 ? (
                <div style={{ color: "#999", fontSize: "14px" }}>No templates yet.</div>
            ) : (
                <div style={{ overflowX: "auto", border: "1px solid #e0e0e0", borderRadius: "6px" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                            <tr>
                                <th style={thLeftStyle}>Name</th>
                                <th style={thLeftStyle}>Lines</th>
                                <th style={thLeftStyle}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {templates.map(t => (
                                <tr key={t.id}>
                                    <td style={tdLeftStyle}>{t.name}</td>
                                    <td style={tdLeftStyle}>{t.line_count}</td>
                                    <td style={tdLeftStyle}>
                                        <button style={smallBtnStyle} onClick={() => startEdit(t.id)}>Edit</button>
                                        {" "}
                                        <button style={smallBtnStyle} onClick={() => handleDelete(t.id)}>Delete</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default TimesheetTemplates;
